import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  Alert,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  CssBaseline,
  Divider,
  FormControlLabel,
  Grid,
  MenuItem,
  Paper,
  Snackbar,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import SaveIcon from "@mui/icons-material/Save";

import GoogleMapView from "../components/GoogleMapView";
import ZoneToolbar from "../components/ZoneToobar";
import { getZoneByIdUseCase } from "../domain/usecases/getZoneByIdUseCase";
import { updateZoneUseCase } from "../domain/usecases/updateZoneUseCase";
import Topbar from "../../../components/topbar/TopBar";
import { getCountryListUseCase } from "../../geography_catalog/country/domain/usecases/getCountryListUseCase";
import { getDepartmentListByCountryIdUseCase } from "../../geography_catalog/department/domain/usecases/getDepartmentListByCountryIdUseCase";
import { getProvinceListByDepartmentIdUseCase } from "../../geography_catalog/province/domain/usecases/getProvinceListByDepartmentIdUseCase";

function getZonePolygon(zone) {
  return Array.isArray(zone?.poligone) ? zone.poligone : zone?.polygon || [];
}

function getZoneCells(zone) {
  return Array.isArray(zone?.cells) ? zone.cells : [];
}

function createForm(zone) {
  return {
    countryId: zone?.countryId ? String(zone.countryId) : "",
    departmentId: zone?.departmentId ? String(zone.departmentId) : "",
    provinceId: zone?.provinceId ? String(zone.provinceId) : "",
    name: zone?.name || "",
    description: zone?.description || "",
    isSpecial: Boolean(zone?.isSpecial),
    multiplier:
      zone?.multiplier === null || zone?.multiplier === undefined
        ? "1.00"
        : String(zone.multiplier),
    resolution: zone?.resolution || 8,
  };
}

function normalizeText(value) {
  return String(value || "")
    .trim()
    .toLowerCase();
}

function findMatchingId(records, zoneValue, fallbackId) {
  if (fallbackId) {
    const matchedById = records.find(
      (record) => String(record.id) === String(fallbackId),
    );

    if (matchedById) {
      return String(matchedById.id);
    }
  }

  const target = normalizeText(zoneValue);

  if (!target) {
    return "";
  }

  const matchedByName = records.find(
    (record) =>
      normalizeText(record.name) === target || normalizeText(record.code) === target,
  );

  return matchedByName ? String(matchedByName.id) : "";
}

function isSuccessResponse(response) {
  const message = response?.message?.toLowerCase() || "";

  return (
    response?.success === true ||
    message.includes("success") ||
    message.includes("correct")
  );
}

export default function ZoneUpdatePage() {
  const { id } = useParams();
  const navigate = useNavigate();

  const [form, setForm] = useState(createForm());
  const [polygon, setPolygon] = useState([]);
  const [initialPolygon, setInitialPolygon] = useState([]);
  const [cells, setCells] = useState([]);
  const [countries, setCountries] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [provinces, setProvinces] = useState([]);
  const [clearTrigger, setClearTrigger] = useState(0);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState({
    open: false,
    type: "success",
    text: "",
  });

  useEffect(() => {
    const loadZone = async () => {
      try {
        setLoading(true);
        const [zone, countryRecords] = await Promise.all([
          getZoneByIdUseCase(id),
          getCountryListUseCase(),
        ]);
        const zonePolygon = getZonePolygon(zone);
        const zoneCells = getZoneCells(zone);
        const selectedCountryId =
          findMatchingId(countryRecords, zone?.country, zone?.countryId) ||
          (countryRecords[0] ? String(countryRecords[0].id) : "");

        let departmentRecords = [];
        if (selectedCountryId) {
          departmentRecords = await getDepartmentListByCountryIdUseCase(
            selectedCountryId,
          );
        }

        const selectedDepartmentId =
          findMatchingId(
            departmentRecords,
            zone?.department || zone?.departament,
            zone?.departmentId || zone?.departamentId,
          ) || (departmentRecords[0] ? String(departmentRecords[0].id) : "");

        let provinceRecords = [];
        if (selectedDepartmentId) {
          provinceRecords = await getProvinceListByDepartmentIdUseCase(
            selectedDepartmentId,
          );
        }

        const selectedProvinceId =
          findMatchingId(provinceRecords, zone?.province, zone?.provinceId) ||
          (provinceRecords[0] ? String(provinceRecords[0].id) : "");

        setCountries(countryRecords);
        setDepartments(departmentRecords);
        setProvinces(provinceRecords);
        setForm({
          ...createForm(zone),
          countryId: selectedCountryId,
          departmentId: selectedDepartmentId,
          provinceId: selectedProvinceId,
        });
        setPolygon(zonePolygon);
        setInitialPolygon(zonePolygon);
        setCells(zoneCells);
      } catch (error) {
        setMessage({
          open: true,
          type: "error",
          text: error.message || "No se pudo cargar la zona.",
        });
      } finally {
        setLoading(false);
      }
    };

    loadZone();
  }, [id]);

  useEffect(() => {
    const loadDepartments = async () => {
      if (!form.countryId) {
        setDepartments([]);
        setProvinces([]);
        setForm((prev) => ({
          ...prev,
          departmentId: "",
          provinceId: "",
        }));
        return;
      }

      try {
        const records = await getDepartmentListByCountryIdUseCase(form.countryId);
        setDepartments(records);
        setForm((prev) => {
          const currentExists = records.some(
            (record) => String(record.id) === String(prev.departmentId),
          );

          return {
            ...prev,
            departmentId: currentExists
              ? prev.departmentId
              : records[0]
                ? String(records[0].id)
                : "",
            provinceId: currentExists ? prev.provinceId : "",
          };
        });
      } catch (error) {
        setDepartments([]);
        setProvinces([]);
        setMessage({
          open: true,
          type: "error",
          text: error.message || "No se pudo cargar la lista de departamentos.",
        });
      }
    };

    if (!loading) {
      loadDepartments();
    }
  }, [form.countryId, loading]);

  useEffect(() => {
    const loadProvinces = async () => {
      if (!form.departmentId) {
        setProvinces([]);
        setForm((prev) => ({
          ...prev,
          provinceId: "",
        }));
        return;
      }

      try {
        const records = await getProvinceListByDepartmentIdUseCase(
          form.departmentId,
        );
        setProvinces(records);
        setForm((prev) => {
          const currentExists = records.some(
            (record) => String(record.id) === String(prev.provinceId),
          );

          return {
            ...prev,
            provinceId: currentExists
              ? prev.provinceId
              : records[0]
                ? String(records[0].id)
                : "",
          };
        });
      } catch (error) {
        setProvinces([]);
        setMessage({
          open: true,
          type: "error",
          text: error.message || "No se pudo cargar la lista de provincias.",
        });
      }
    };

    if (!loading) {
      loadProvinces();
    }
  }, [form.departmentId, loading]);

  const handleChange = (event) => {
    const { name, value } = event.target;

    setForm((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleClearMap = () => {
    setInitialPolygon([]);
    setClearTrigger((prev) => prev + 1);
  };

  const handleSave = async () => {
    const payload = {
      id,
      countryId: Number(form.countryId),
      departmentId: Number(form.departmentId),
      provinceId: Number(form.provinceId),
      name: form.name,
      description: form.description,
      isSpecial: Boolean(form.isSpecial),
      multiplier: Number(form.multiplier),
      resolution: Number(form.resolution),
      polygon,
      cells,
    };

    try {
      setSaving(true);
      const response = await updateZoneUseCase(payload);

      if (!isSuccessResponse(response)) {
        throw new Error(response?.message || "No se pudo actualizar la zona.");
      }

      setMessage({
        open: true,
        type: "success",
        text: "Zona geoespacial actualizada correctamente.",
      });

      window.setTimeout(() => {
        navigate("/mapstest/lista");
      }, 1200);
    } catch (error) {
      setMessage({
        open: true,
        type: "error",
        text: error.message || "No se pudo actualizar la zona.",
      });
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <CssBaseline />
      <main className="content">
        <Topbar />
        <Box sx={{ minHeight: "100vh", p: 1 }}>
          <Stack
            direction={{ xs: "column", md: "row" }}
            alignItems={{ xs: "stretch", md: "center" }}
            justifyContent="space-between"
            spacing={2}
            sx={{ mb: 3 }}
          >
            <Box>
              <Typography variant="h4" fontWeight={800}>
                Actualizar zona
              </Typography>
              <Typography variant="body2" color="text.secondary" mt={0.5}>
                Modifica la información y ajusta los vértices del polígono.
              </Typography>
            </Box>

            <Stack direction="row" spacing={1.5}>
              <Button
                variant="outlined"
                color="success"
                startIcon={<ArrowBackIcon />}
                onClick={() => navigate("/admin/zona-especial/lista")}
              >
                Volver
              </Button>
              <Button
                variant="contained"
                startIcon={<SaveIcon />}
                onClick={handleSave}
                disabled={loading || saving}
              >
                {saving ? "Actualizando..." : "Actualizar"}
              </Button>
            </Stack>
          </Stack>

          {loading ? (
            <Paper
              elevation={2}
              sx={{
                minHeight: 420,
                borderRadius: 3,
                display: "grid",
                placeItems: "center",
              }}
            >
              <Stack alignItems="center" spacing={2}>
                <CircularProgress />
                <Typography color="text.secondary">Cargando zona...</Typography>
              </Stack>
            </Paper>
          ) : (
            <Grid container spacing={3}>
              <Grid item xs={12} md={4} lg={3}>
                <Paper
                  elevation={2}
                  sx={{
                    borderRadius: 3,
                    overflow: "hidden",
                  }}
                >
                  <Box sx={{ p: 3 }}>
                    <Typography variant="h6" fontWeight={700}>
                      Datos de la zona
                    </Typography>
                    <Typography variant="body2" color="text.secondary" mt={1}>
                      Los cambios se guardarán sobre la zona seleccionada.
                    </Typography>

                    <Divider sx={{ my: 2 }} />

                    <Stack spacing={2}>
                      <TextField
                        fullWidth
                        select
                        label="Pais"
                        name="countryId"
                        value={form.countryId}
                        onChange={handleChange}
                      >
                        {countries.map((country) => (
                          <MenuItem key={country.id} value={String(country.id)}>
                            {country.name} - {country.code}
                          </MenuItem>
                        ))}
                      </TextField>
                      <TextField
                        fullWidth
                        select
                        label="Departamento"
                        name="departmentId"
                        value={form.departmentId}
                        onChange={handleChange}
                      >
                        {departments.map((department) => (
                          <MenuItem key={department.id} value={String(department.id)}>
                            {department.name} - {department.code}
                          </MenuItem>
                        ))}
                      </TextField>
                      <TextField
                        fullWidth
                        select
                        label="Provincia"
                        name="provinceId"
                        value={form.provinceId}
                        onChange={handleChange}
                      >
                        {provinces.map((province) => (
                          <MenuItem key={province.id} value={String(province.id)}>
                            {province.name} - {province.code}
                          </MenuItem>
                        ))}
                      </TextField>
                      <TextField
                        fullWidth
                        label="Nombre de la Zona"
                        name="name"
                        value={form.name}
                        onChange={handleChange}
                      />
                      <TextField
                        fullWidth
                        multiline
                        rows={4}
                        label="Descripción breve"
                        name="description"
                        value={form.description}
                        onChange={handleChange}
                      />
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={Boolean(form.isSpecial)}
                            onChange={(event) =>
                              setForm((prev) => ({
                                ...prev,
                                isSpecial: event.target.checked,
                              }))
                            }
                            sx={{
                              color: "#165c53",
                              "&.Mui-checked": {
                                color: "#165c53",
                              },
                            }}
                          />
                        }
                        label="Es especial"
                        sx={{
                          mx: 0,
                          "& .MuiFormControlLabel-label": {
                            color: "#000",
                            fontWeight: 600,
                          },
                        }}
                      />
                      <TextField
                        fullWidth
                        type="number"
                        label="Multiplicador"
                        name="multiplier"
                        value={form.multiplier}
                        onChange={handleChange}
                        inputProps={{ min: "1", step: "0.01" }}
                      />
                      <TextField
                        fullWidth
                        select
                        label="Resolución H3"
                        name="resolution"
                        value={form.resolution}
                        onChange={handleChange}
                      >
                        <MenuItem value={7}>7 - Cobertura amplia</MenuItem>
                        <MenuItem value={8}>8 - Recomendado</MenuItem>
                        <MenuItem value={9}>9 - Alta precisión</MenuItem>
                        <MenuItem value={10}>10 - Muy alta precisión</MenuItem>
                      </TextField>
                    </Stack>

                    <Divider sx={{ my: 2 }} />

                    <Button
                      fullWidth
                      variant="contained"
                      startIcon={<SaveIcon />}
                      onClick={handleSave}
                      disabled={saving}
                    >
                      {saving ? "Actualizando..." : "Guardar cambios"}
                    </Button>
                  </Box>

                  {/* <ZoneStatistics vertices={polygon.length} /> */}

                  <Box sx={{ px: 3, pb: 3 }}>
                    <Typography variant="body2" color="text.secondary">
                      Celdas recibidas: {cells.length}
                    </Typography>
                  </Box>
                </Paper>
              </Grid>

              <Grid item xs={12} md={8} lg={9}>
                <Paper
                  elevation={2}
                  sx={{
                    position: "relative",
                    height: {
                      xs: 460,
                      sm: 540,
                      md: "calc(100vh - 154px)",
                    },
                    minHeight: 460,
                    borderRadius: 3,
                    overflow: "hidden",
                  }}
                >
                  <ZoneToolbar
                    mode="draw"
                    onDrawMode={() => {}}
                    onMoveMode={() => {}}
                    onClearMap={handleClearMap}
                    onPreviewH3={() => {
                      console.log("Update polygon:", polygon);
                    }}
                  />

                  <GoogleMapView
                    initialPolygon={initialPolygon}
                    onPolygonChange={setPolygon}
                    clearTrigger={clearTrigger}
                  />
                </Paper>
              </Grid>
            </Grid>
          )}

          <Snackbar
            open={message.open}
            autoHideDuration={4000}
            onClose={() =>
              setMessage((prev) => ({
                ...prev,
                open: false,
              }))
            }
            anchorOrigin={{
              vertical: "top",
              horizontal: "right",
            }}
          >
            <Alert
              severity={message.type}
              variant="filled"
              onClose={() =>
                setMessage((prev) => ({
                  ...prev,
                  open: false,
                }))
              }
            >
              {message.text}
            </Alert>
          </Snackbar>
        </Box>
      </main>
    </>
  );
}
